import classNames from "classnames";
import { upperFirst } from "lodash";
import { useActivityPageContext } from "PFApp/activities/show/activity_page_context";
import AppContext from "PFApp/app_context";
import { Button } from "PFComponents/button";
import ConfirmModal from "PFComponents/confirm/confirm_modal";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import {
  useShortlistFill,
  useShortlistFillAndBook,
  useShortlistReject
} from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import { FeatureFlag } from "PFTypes";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./assignment_actions.module.scss";

const AssignmentActions = ({ shortlistItem }) => {
  const { showAsInternalExperience, fetchRolesVacanciesPage } = useActivityPageContext();
  const { dispatch } = useContext(AppContext);
  const { t } = useTranslation("activities");
  const { getFormattedErrors } = useResponseErrors();
  const isAutoBookingCreationEnabled = useIsFeatureEnabled()(FeatureFlag.AutoBookingCreation);

  const { mutate: fillShortlist } = useShortlistFill();
  const { mutate: fillAndBookShortlist } = useShortlistFillAndBook();
  const { mutate: rejectShortlist } = useShortlistReject();

  const [state, setState] = useState(shortlistItem.state);
  const [showDeclineConfirm, setShowDeclineConfirm] = useState(false);

  const isAccepted = state === "filled";
  const isDeclined = state === "rejected";

  const renderGrowl = (message, options = {}) =>
    dispatch({
      type: GROWL_ACTIONS.GROWL_RENDER,
      payload: {
        message: message,
        kind: options.kind || "success",
        url: options.url
      }
    });

  const errorMessage = ({ response }) => {
    const errors = getFormattedErrors(response, {});
    return errors.id ? upperFirst(errors.id) : t("somethingWentWrong");
  };

  const handleAccept = () => {
    const options = {
      onSuccess: (item) => {
        fetchRolesVacanciesPage();
        setState(item.state);
        if (showAsInternalExperience) {
          renderGrowl(t("show.parts.assignmentAccepted"), {
            url: "/profile/edit/internal_experience"
          });
        } else {
          renderGrowl(t("show.parts.assignmentSubmitted"));
        }
      },
      onError: (response) => renderGrowl(errorMessage(response), { kind: "error" })
    };

    if (isAutoBookingCreationEnabled) {
      fillAndBookShortlist({ shortlistId: shortlistItem.id }, options);
      return;
    }

    fillShortlist(shortlistItem.id, options);
  };

  const handleDecline = () => {
    rejectShortlist(shortlistItem.id, {
      onSuccess: (item) => {
        fetchRolesVacanciesPage();
        setState(item.state);
        renderGrowl(t("show.parts.assignmentSubmitted"));
      },
      onError: (response) => renderGrowl(errorMessage(response), { kind: "error" })
    });
  };

  return (
    <div className={css.container}>
      <Button
        icon="check"
        text={isAccepted ? t("accepted") : t("accept")}
        className={classNames(css.button, { [css.buttonAccepted]: isAccepted })}
        kind="secondary"
        qaId="role-accept"
        onClick={handleAccept}
      />
      <Button
        icon="cross"
        text={isDeclined ? t("declined") : t("decline")}
        className={classNames(css.button, { [css.buttonDeclined]: isDeclined })}
        kind="secondary"
        qaId="role-decline"
        onClick={() => setShowDeclineConfirm(true)}
      />
      {showDeclineConfirm && (
        <ConfirmModal
          labelOK="Confirm"
          handleOK={handleDecline}
          handleClose={() => setShowDeclineConfirm(false)}
        >
          {t("show.parts.assignmentConfirmDecline")}
        </ConfirmModal>
      )}
    </div>
  );
};

export default AssignmentActions;

AssignmentActions.propTypes = {
  shortlistItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    state: PropTypes.string.isRequired
  })
};
